import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { PlayCircle } from "lucide-react"
import { ChatWidget } from "@/components/chat/chat-widget"
import { CollaboratorList } from "@/components/collaborator-list"
import AssetProjectionChart from "@/components/individual-asset-projection"
import { TutorialVideoModal } from "@/components/modals/tutorial-video-modal"
import { Page } from "@/components/page"
import { PageLoader } from "@/components/page-loader"
import { Button } from "@/components/ui/button"
import { Typography } from "@/components/ui/typography"
import { EstateNextSteps } from "@/components/widgets/estate-next-steps"
import { EstatePlanningChecklist } from "@/components/widgets/estate-planning-checklist"
import { EstateReadinessScore } from "@/components/widgets/estate-readiness-score"
import { EstateSummaryCards } from "@/components/widgets/estate-summary-cards"
import { TasksPanel } from "@/components/widgets/tasks-panel"

export const Route = createFileRoute("/_authed/_individual/personal/dashboard")({
  component: IndividualDashboard,
})

function IndividualDashboard() {
  const { data: user, isLoading: userLoading } = trpc.users.me.useQuery()
  const estateId = user?.estateId ?? ""
  const enabled = Boolean(estateId)

  // Keep these (used by cards + chart)
  const { data: assets } = trpc.assets.list.useQuery({ estateId }, { enabled })

  const dashboardHeader = (
    <div className="flex w-full items-center justify-between gap-4">
      <Typography as="h1" size="2xl">
        My Estate Dashboard
      </Typography>

      <TutorialVideoModal
        triggerSlot={
          <Button type="button" variant="default">
            <PlayCircle className="md:mr-1 size-4" aria-hidden="true" />
            <span className="hidden md:inline">Watch tutorial</span>
          </Button>
        }
      />
    </div>
  )

  if (userLoading) {
    return (
      <Page headerSlot={dashboardHeader}>
        <PageLoader />
      </Page>
    )
  }

  return (
    <Page headerSlot={dashboardHeader}>
      <div className="space-y-6">
        <EstateSummaryCards estateId={estateId} variant="individual" />

        <div className="grid gap-6 lg:grid-cols-2">
          <EstateReadinessScore estateId={estateId} variant="individual" />

          <EstateNextSteps estateId={estateId} variant="individual" />
        </div>

        <div className="grid items-stretch gap-6 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <AssetProjectionChart assets={assets ?? []} />
          </div>

          <TasksPanel estateId={estateId} />
        </div>
        <EstatePlanningChecklist estateId={estateId} />
        <div className="grid gap-4 grid-cols-2">
          <div className="col-span-2 md:col-span-1">
            <CollaboratorList />
          </div>
        </div>
      </div>
      <ChatWidget />
    </Page>
  )
}
